<template>
  <div class="settings-section">
    <h2 class="section-title">{{ $t('settings.businessInfo', 'Business Information') }}</h2>
    <p class="section-description">{{ $t('settings.businessInfoDesc', 'Basic information about your business') }}</p>

    <div class="settings-form">
      <div class="form-field">
        <IntercomInput
          v-model="formData.name"
          :label="$t('settings.businessName', 'Business Name')"
          required
        />
      </div>
      <div class="form-field">
        <IntercomInput
          v-model="formData.address"
          type="textarea"
          :label="$t('settings.address', 'Address')"
          :rows="3"
        />
      </div>
      <div class="form-row">
        <div class="form-field">
          <IntercomPhoneInput
            v-model="formData.phone"
            :label="$t('settings.phone', 'Phone')"
            :error="phoneError"
          />
        </div>
        <div class="form-field">
          <IntercomEmailInput
            v-model="formData.email"
            :label="$t('settings.email', 'Email')"
            :error="emailError"
            @update:valid="onEmailValidChange"
          />
        </div>
      </div>

      <!-- Default Locale -->
      <IntercomSelect
        v-model="formData.default_locale"
        :label="$t('settings.defaultLocale', 'Default Language')"
        :options="localeOptions"
      />
    </div>

    <!-- Bank Accounts Section -->
    <h2 class="section-title bank-section-title">{{ $t('settings.bankDetails', 'Bank Details') }}</h2>
    <p class="section-description">{{ $t('settings.bankDetailsDesc', 'Bank account details shown on invoices') }}</p>

    <!-- Bank Account Cards -->
    <div class="bank-accounts-list">
      <div
        v-for="account in bankAccounts"
        :key="account.id"
        :class="['bank-account-card', { 'is-default': account.is_default }]"
      >
        <div class="bank-card-header">
          <div class="bank-card-title">
            <span class="bank-label">{{ account.label }}</span>
            <span v-if="account.is_default" class="default-badge">Default</span>
          </div>
          <div class="bank-card-actions">
            <button
              v-if="!account.is_default"
              class="btn-text"
              @click="handleSetDefault(account.id)"
              :title="$t('settings.setDefault', 'Set as default')"
            >Set Default</button>
            <button
              class="btn-text"
              @click="startEditAccount(account)"
            >Edit</button>
            <button
              class="btn-text btn-danger"
              @click="handleDeleteAccount(account.id)"
            >Delete</button>
          </div>
        </div>
        <div class="bank-card-body">
          <div class="bank-detail-row">
            <span class="bank-detail-key">Bank</span>
            <span class="bank-detail-val">{{ account.bank_name }}</span>
          </div>
          <div class="bank-detail-row">
            <span class="bank-detail-key">A/C No.</span>
            <span class="bank-detail-val">{{ account.account_number }}</span>
          </div>
          <div v-if="account.branch" class="bank-detail-row">
            <span class="bank-detail-key">Branch</span>
            <span class="bank-detail-val">{{ account.branch }}</span>
          </div>
          <div class="bank-detail-row">
            <span class="bank-detail-key">IFSC</span>
            <span class="bank-detail-val">{{ account.ifsc_code }}</span>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="bankAccounts.length === 0" class="bank-empty">
        No bank accounts configured. Add one to show bank details on invoices.
      </div>
    </div>

    <!-- Add / Edit Bank Account Form -->
    <div v-if="showBankForm" class="bank-form-section">
      <h3 class="bank-form-title">{{ editingAccountId ? 'Edit Bank Account' : 'Add Bank Account' }}</h3>
      <div class="settings-form">
        <div class="form-row">
          <div class="form-field">
            <IntercomInput
              v-model="bankFormData.label"
              label="Label"
              placeholder="e.g. SBI Current A/C"
              required
            />
          </div>
          <div class="form-field">
            <IntercomInput
              v-model="bankFormData.bank_name"
              label="Bank Name"
              placeholder="e.g. State Bank of India"
              required
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <IntercomInput
              v-model="bankFormData.account_number"
              label="Account Number"
              placeholder="e.g. 1234567890"
              required
            />
          </div>
          <div class="form-field">
            <IntercomInput
              v-model="bankFormData.ifsc_code"
              label="IFSC Code"
              placeholder="e.g. SBIN0001234"
              required
              maxlength="11"
              :hint="ifscHint"
            />
          </div>
        </div>
        <div class="form-field">
          <IntercomInput
            v-model="bankFormData.branch"
            label="Branch"
            placeholder="e.g. Dindigul Main Branch"
          />
        </div>
        <div class="bank-form-actions">
          <button class="btn-discard" @click="cancelBankForm">Cancel</button>
          <IntercomButton variant="primary" @click="handleSaveBankAccount" :disabled="isSavingBank">
            {{ isSavingBank ? 'Saving...' : (editingAccountId ? 'Update' : 'Add Account') }}
          </IntercomButton>
        </div>
      </div>
    </div>

    <!-- Add Button (when form is hidden and < 5 accounts) -->
    <button
      v-if="!showBankForm && bankAccounts.length < 5"
      class="btn-add-bank"
      @click="startAddAccount"
    >
      + Add Bank Account
    </button>

    <!-- Invoice Editing -->
    <h2 class="section-title bank-section-title">Invoice Editing</h2>
    <p class="section-description">Control when invoices can be edited after creation</p>
    <div class="settings-form">
      <div class="form-field">
        <IntercomInput
          v-model.number="editLockDays"
          type="number"
          label="Edit Lock (days)"
          placeholder="0 = no lock"
          :min="0"
        />
        <p class="field-hint">Invoices older than this many days cannot be edited. Set to 0 to allow editing anytime.</p>
      </div>
      <div class="bank-form-actions">
        <IntercomButton variant="primary" @click="handleSaveEditLock" :disabled="isSavingEditLock">
          {{ isSavingEditLock ? 'Saving...' : 'Save' }}
        </IntercomButton>
      </div>
    </div>

    <!-- Business Type (HSN Priority) -->
    <h2 class="section-title bank-section-title">Business Type</h2>
    <p class="section-description">Helps show relevant GST/HSN codes first when adding products</p>
    <div class="settings-form">
      <div class="form-field">
        <IntercomSelect
          v-model="businessType"
          label="What does your business sell?"
          :options="businessTypeOptions"
          placeholder="Select business type"
        />
      </div>
      <div class="bank-form-actions">
        <IntercomButton variant="primary" @click="handleSaveBusinessType" :disabled="isSavingBusinessType">
          {{ isSavingBusinessType ? 'Saving...' : 'Save' }}
        </IntercomButton>
      </div>
    </div>

    <!-- Due Date Settings -->
    <h2 class="section-title bank-section-title">Due Date</h2>
    <p class="section-description">Configure due date behavior on invoices</p>
    <div class="settings-form">
      <div class="form-field">
        <label class="toggle-label">
          <input type="checkbox" v-model="showDueDate" class="toggle-checkbox" />
          <span>Show due date on invoices</span>
        </label>
        <p class="field-hint">When disabled, due date will not appear on invoice forms or printed bills.</p>
      </div>
      <div v-if="showDueDate" class="form-field">
        <IntercomInput
          v-model.number="defaultDueDays"
          type="number"
          label="Default Due Days"
          placeholder="30"
          :min="0"
          :max="365"
        />
        <p class="field-hint">Number of days from invoice date to set as default due date. Set to 0 for no default.</p>
      </div>
      <div class="bank-form-actions">
        <IntercomButton variant="primary" @click="handleSaveDueDate" :disabled="isSavingDueDate">
          {{ isSavingDueDate ? 'Saving...' : 'Save' }}
        </IntercomButton>
      </div>
    </div>

    <!-- Save Footer -->
    <div class="save-footer" v-if="isDirty || saveStatus">
      <button
        v-if="isDirty"
        class="btn-discard"
        @click="handleDiscard"
      >
        {{ $t('settings.discard', 'Discard') }}
      </button>
      <IntercomButton
        variant="primary"
        :class="{ saved: saveStatus === 'saved' }"
        :disabled="!isDirty && saveStatus !== 'saved'"
        @click="handleSave"
      >
        {{ isSaving ? $t('common.saving', 'Saving...') : saveStatus === 'saved' ? $t('common.saved', 'Saved') + ' ✓' : $t('settings.saveChanges', 'Save Changes') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../../composables/useBilling.js'
import { IntercomInput, IntercomPhoneInput, IntercomEmailInput, IntercomButton, IntercomSelect } from '@shared/components/intercom'

const { t } = useI18n()
const toast = useToast()
const { getBusiness, updateBusiness, listBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount, setDefaultBankAccount, getBillingSettings, updateBillingSettings } = useBilling()

const localeOptions = [
  { value: 'en_IN', label: 'English (India)' },
  { value: 'ta_IN', label: 'தமிழ் (Tamil)' },
]

const originalData = ref({})
const fullBusinessData = ref({}) // Store full business data including tax_regime
const isSaving = ref(false)
const saveStatus = ref(null)
const emailError = ref('')
const phoneError = ref('')

// Email/Phone validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRegex = /^\d{10}$/

const formData = reactive({
  name: '',
  address: '',
  phone: '',
  email: '',
  default_locale: 'ta_IN',
})

// Bank accounts state
const bankAccounts = ref([])
const showBankForm = ref(false)
const editingAccountId = ref(null)
const isSavingBank = ref(false)
const bankFormData = reactive({
  label: '',
  bank_name: '',
  account_number: '',
  branch: '',
  ifsc_code: '',
})

// Track dirty state
const isDirty = computed(() => {
  return JSON.stringify(formData) !== JSON.stringify(originalData.value)
})

// Clear errors when user types
watch(() => formData.email, () => {
  emailError.value = ''
})

watch(() => formData.phone, () => {
  phoneError.value = ''
})

const onEmailValidChange = (isValid) => {
  if (!isValid && formData.email) {
    emailError.value = t('errors.invalidEmail', 'Please enter a valid email address')
  } else {
    emailError.value = ''
  }
}

const handleSave = async () => {
  if (!isDirty.value) return

  // Validate
  if (!formData.name) {
    toast.warning(t('settings.nameRequired', 'Business name is required'))
    return
  }

  // Validate email if provided
  if (formData.email && !emailRegex.test(formData.email)) {
    emailError.value = t('errors.invalidEmail', 'Please enter a valid email address')
    return
  }

  // Validate phone if provided
  if (formData.phone && !phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
    phoneError.value = t('errors.invalidPhone', 'Please enter a valid 10-digit phone number')
    return
  }

  isSaving.value = true
  try {
    // Merge form data with full business data to preserve tax settings
    const updateData = {
      ...fullBusinessData.value,
      ...formData,
    }
    await updateBusiness(updateData)
    // Update original data to match current
    originalData.value = { ...formData }
    saveStatus.value = 'saved'
    toast.success(t('settings.businessUpdated', 'Business settings updated successfully'))
    setTimeout(() => {
      saveStatus.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to save business settings:', error)
    toast.error(t('settings.updateFailed', 'Failed to update business settings'))
  } finally {
    isSaving.value = false
  }
}

const handleDiscard = () => {
  Object.assign(formData, originalData.value)
  emailError.value = ''
  phoneError.value = ''
}

const fetchBusiness = async () => {
  try {
    const business = await getBusiness()
    fullBusinessData.value = { ...business }

    // Populate form with business data
    formData.name = business.name || ''
    formData.address = business.address || ''
    formData.phone = business.phone || ''
    formData.email = business.email || ''
    formData.default_locale = business.default_locale || 'ta_IN'

    // Store original for dirty checking
    originalData.value = { ...formData }
  } catch (error) {
    console.error('Failed to fetch business settings:', error)
    toast.error(t('settings.fetchFailed', 'Failed to load business settings'))
  }
}

const fetchBankAccounts = async () => {
  try {
    const res = await listBankAccounts()
    bankAccounts.value = res?.data || []
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
  }
}

const ifscHint = computed(() => {
  const len = bankFormData.ifsc_code.length
  if (!len) return ''
  if (len === 11) return '11/11'
  const remaining = 11 - len
  return `${len}/11 — ${remaining} more`
})

// Bank account management
const resetBankForm = () => {
  bankFormData.label = ''
  bankFormData.bank_name = ''
  bankFormData.account_number = ''
  bankFormData.branch = ''
  bankFormData.ifsc_code = ''
  editingAccountId.value = null
}

const startAddAccount = () => {
  resetBankForm()
  showBankForm.value = true
}

const startEditAccount = (account) => {
  editingAccountId.value = account.id
  bankFormData.label = account.label
  bankFormData.bank_name = account.bank_name
  bankFormData.account_number = account.account_number
  bankFormData.branch = account.branch || ''
  bankFormData.ifsc_code = account.ifsc_code
  showBankForm.value = true
}

const cancelBankForm = () => {
  showBankForm.value = false
  resetBankForm()
}

const handleSaveBankAccount = async () => {
  if (!bankFormData.label || !bankFormData.bank_name || !bankFormData.account_number || !bankFormData.ifsc_code) {
    toast.warning('Please fill in all required fields')
    return
  }
  if (bankFormData.ifsc_code.length !== 11) {
    toast.warning('IFSC code must be exactly 11 characters')
    return
  }

  isSavingBank.value = true
  try {
    const payload = {
      label: bankFormData.label,
      bank_name: bankFormData.bank_name,
      account_number: bankFormData.account_number,
      branch: bankFormData.branch || null,
      ifsc_code: bankFormData.ifsc_code,
      is_default: bankAccounts.value.length === 0,
    }

    if (editingAccountId.value) {
      await updateBankAccount(editingAccountId.value, payload)
      toast.success('Bank account updated')
    } else {
      await createBankAccount(payload)
      toast.success('Bank account added')
    }

    cancelBankForm()
    await fetchBankAccounts()
  } catch (error) {
    console.error('Failed to save bank account:', error)
    toast.error(error?.message || 'Failed to save bank account')
  } finally {
    isSavingBank.value = false
  }
}

const handleDeleteAccount = async (id) => {
  if (!confirm('Delete this bank account?')) return
  try {
    await deleteBankAccount(id)
    toast.success('Bank account deleted')
    await fetchBankAccounts()
  } catch (error) {
    console.error('Failed to delete bank account:', error)
    toast.error('Failed to delete bank account')
  }
}

const handleSetDefault = async (id) => {
  try {
    await setDefaultBankAccount(id)
    toast.success('Default bank account updated')
    await fetchBankAccounts()
  } catch (error) {
    console.error('Failed to set default:', error)
    toast.error('Failed to set default bank account')
  }
}

// Invoice edit lock
const editLockDays = ref(0)
const isSavingEditLock = ref(false)

const fetchEditLockDays = async () => {
  try {
    const settings = await getBillingSettings()
    const data = settings?.data || settings
    editLockDays.value = data?.invoice_edit_lock_days || 0
  } catch {
    editLockDays.value = 0
  }
}

const handleSaveEditLock = async () => {
  isSavingEditLock.value = true
  try {
    await updateBillingSettings({ invoice_edit_lock_days: editLockDays.value || 0 })
    toast.success('Edit lock setting saved')
  } catch (error) {
    toast.error('Failed to save: ' + (error.message || 'Unknown error'))
  } finally {
    isSavingEditLock.value = false
  }
}

// Business type settings (HSN priority)
const businessType = ref('')
const isSavingBusinessType = ref(false)
const businessTypeOptions = [
  { value: '', label: 'Not set' },
  { value: 'grocery', label: 'Grocery / Kirana' },
  { value: 'stationery', label: 'Stationery / Book Shop' },
  { value: 'medical', label: 'Medical / Pharmacy' },
  { value: 'jewellery', label: 'Jewellery / Gold' },
  { value: 'garments', label: 'Garments / Textiles' },
  { value: 'electronics', label: 'Electronics / Mobile' },
  { value: 'hardware', label: 'Hardware / Paint' },
  { value: 'restaurant', label: 'Restaurant / Bakery / Sweets' },
  { value: 'cosmetics', label: 'Cosmetics / Beauty' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'auto_parts', label: 'Auto Parts / Garage' },
  { value: 'general', label: 'General / Household' },
  { value: 'gift_shop', label: 'Gift Shop / Awards' },
]

const handleSaveBusinessType = async () => {
  isSavingBusinessType.value = true
  try {
    await updateBillingSettings({ business_type: businessType.value })
    toast.success('Business type saved')
  } catch (error) {
    toast.error('Failed to save: ' + (error.message || 'Unknown error'))
  } finally {
    isSavingBusinessType.value = false
  }
}

// Due date settings
const showDueDate = ref(true)
const defaultDueDays = ref(30)
const isSavingDueDate = ref(false)

const fetchDueDateSettings = async () => {
  try {
    const settings = await getBillingSettings()
    const data = settings?.data || settings
    showDueDate.value = data?.show_due_date !== false // default true
    defaultDueDays.value = data?.default_due_days ?? 30
    businessType.value = data?.business_type || ''
  } catch {
    showDueDate.value = true
    defaultDueDays.value = 30
  }
}

const handleSaveDueDate = async () => {
  isSavingDueDate.value = true
  try {
    await updateBillingSettings({
      show_due_date: showDueDate.value,
      default_due_days: defaultDueDays.value || 0,
    })
    toast.success('Due date settings saved')
  } catch (error) {
    toast.error('Failed to save: ' + (error.message || 'Unknown error'))
  } finally {
    isSavingDueDate.value = false
  }
}

onMounted(() => {
  fetchBusiness()
  fetchBankAccounts()
  fetchEditLockDays()
  fetchDueDateSettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.bank-section-title {
  margin-top: var(--space-8);
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

/* Save Footer */
.save-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  margin: var(--space-6) calc(-1 * var(--space-6)) calc(-1 * var(--space-6));
  background: var(--color-white);
  border-top: 1px solid var(--color-gray-200);
}

.btn-discard {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-discard:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.saved {
  background: var(--color-success) !important;
}

/* Bank Accounts */
.bank-accounts-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.bank-account-card {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  background: var(--color-white);
}

.bank-account-card.is-default {
  border-color: var(--color-blue-300);
  background: var(--color-blue-50);
}

.bank-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-3);
}

.bank-card-title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.bank-label {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.default-badge {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--color-blue-700);
  background: var(--color-blue-100);
  padding: 1px 6px;
  border-radius: var(--radius-sm);
}

.bank-card-actions {
  display: flex;
  gap: var(--space-2);
}

.btn-text {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue-600);
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
}

.btn-text:hover {
  text-decoration: underline;
}

.btn-text.btn-danger {
  color: var(--color-red-600);
}

.bank-card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-1);
}

.bank-detail-row {
  display: flex;
  gap: var(--space-2);
}

.bank-detail-key {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  min-width: 52px;
}

.bank-detail-val {
  font-size: var(--font-size-xs);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.bank-empty {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  text-align: center;
  padding: var(--space-6);
  border: 1px dashed var(--color-gray-300);
  border-radius: var(--radius-md);
}

.bank-form-section {
  margin-top: var(--space-4);
  padding: var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  background: var(--color-gray-50);
}

.bank-form-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.bank-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.btn-add-bank {
  width: 100%;
  padding: var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue-600);
  background: transparent;
  border: 1px dashed var(--color-blue-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-top: var(--space-2);
}

.btn-add-bank:hover {
  background: var(--color-blue-50);
  border-color: var(--color-blue-400);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}
</style>
